import { useAtom } from 'jotai';
import type { ExamItemAnswerAiCorrectionViewModel } from '@/shared/api/generated/types';
import { aiBatchSuggestionsAtom } from '../store/atoms/aiBatchSuggestionsAtom';

/**
 * Hook para acessar e atualizar as sugestões de correção por IA obtidas em lote.
 *
 * Encapsula `aiBatchSuggestionsAtom` — nenhum componente deve usar `useAtom`
 * diretamente sobre esse átomo.
 *
 * @returns Leitura (`get`, `has`) e escrita (`setMany`, `remove`) das sugestões, indexadas por `answerId`
 *
 * @example
 * ```ts
 * const { get, setMany, remove, has } = useAiBatchSuggestions();
 * setMany(batchResult);
 * if (has(answerId)) { ... }
 * ```
 */
export function useAiBatchSuggestions() {
  const [suggestions, setSuggestions] = useAtom(aiBatchSuggestionsAtom);

  /** Sugestão armazenada para o `answerId`, ou `undefined` se não houver. */
  function get(
    answerId: number | null | undefined,
  ): ExamItemAnswerAiCorrectionViewModel | undefined {
    if (answerId === null || answerId === undefined) return undefined;
    return suggestions[answerId];
  }

  /** `true` quando há uma sugestão em lote armazenada para o `answerId`. */
  function has(answerId: number | null | undefined): boolean {
    return get(answerId) !== undefined;
  }

  /** Armazena os resultados de uma correção em lote, indexando por `answerId`. */
  function setMany(results: readonly ExamItemAnswerAiCorrectionViewModel[]): void {
    setSuggestions(prev => {
      const next = { ...prev };
      for (const result of results) {
        if (result.answerId !== undefined) next[result.answerId] = result;
      }
      return next;
    });
  }

  /** Remove a sugestão armazenada para o `answerId` (no-op se não houver). */
  function remove(answerId: number | null | undefined): void {
    if (answerId === null || answerId === undefined) return;
    setSuggestions(prev => {
      if (!(answerId in prev)) return prev;
      const next = { ...prev };
      delete next[answerId];
      return next;
    });
  }

  return { get, has, setMany, remove } as const;
}
